import {IconButton} from '@wandb/components';
import {onNextExitFullscreen} from '@wandb/weave/common/util/fullscreen';
import {type Struct} from '@wandb/weave/common/util/types';
import * as React from 'react';
import {useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {Card, Placeholder} from 'semantic-ui-react';

import {labelComponent, makeCaptions} from '../util/media';
import {blobToFile} from '../util/requests';
import {InstrumentedCenteredWaveLoader as Loader} from './../components/utility/InstrumentedLoader';
import {type MediaCardProps} from './MediaCard';
import MessageMediaNotFound from './MessageMediaNotFound';
import {SingleRunLink} from './MultiRunWorkspace/SingleRunMode';
import {useGetTrackingCallbacks} from './PanelMediaBrowser/MediaBrowserPanelTileTrackingContext';

export const MoleculeCard: React.FunctionComponent<MediaCardProps> = React.memo(
  props => {
    const {
      run,
      globalStep,
      mediaKey,
      mediaIndex,
      runSignature,
      height,
      width,
      moleculeConfig,
      tileMedia,
      loading,
      customRunName,
      tileMediaSpec,
      getMediaTracking,
    } = props;

    const blob = tileMedia?.blob;
    const directURL = tileMedia?.directURL;
    const historyRow = tileMedia?.historyRow;

    const stageMountRef = useRef<HTMLDivElement | null>(null);

    const [screenshotURL, setScreenshotURL] = useState<string | null>(null);
    const {trackSuccess, trackError} = useGetTrackingCallbacks(
      tileMedia,
      tileMediaSpec,
      getMediaTracking
    );

    const representation = moleculeConfig?.representation;
    const settings = useMemo(
      () => ({
        representation,
      }),
      [representation]
    );

    // Use async import for ngl code to split bundle
    type NGLLib = typeof import('@wandb/weave/common/util/nglviewerRender');
    const [nglLib, setNglLib] = useState<NGLLib | null>(null);
    useEffect(() => {
      import('@wandb/weave/common/util/nglviewerRender').then(setNglLib);
    }, []);

    useLayoutEffect(() => {
      if (
        nglLib == null ||
        directURL == null ||
        blob == null ||
        stageMountRef.current == null
      ) {
        return;
      }
      const file = blobToFile(blob, directURL);
      if (file == null) {
        return;
      }

      const ext = getMoleculeFileExt(directURL);

      (async () => {
        const imageBlob = await nglLib.moleculeScreenshot(
          file,
          {width, height},
          settings,
          ext,
          trackSuccess,
          trackError
        );
        setScreenshotURL(window.URL.createObjectURL(imageBlob));
      })();
    }, [
      blob,
      directURL,
      width,
      height,
      settings,
      nglLib,
      trackSuccess,
      trackError,
    ]);

    const requestFullscreen = () => {
      if (
        nglLib == null ||
        directURL == null ||
        blob == null ||
        stageMountRef.current == null
      ) {
        return;
      }
      const file = blobToFile(blob, directURL);
      if (file == null) {
        return;
      }

      const screenW = window.screen.width;
      const screenH = window.screen.height;
      const ext = getMoleculeFileExt(directURL);

      const stage = nglLib.moleculeStage(
        stageMountRef.current,
        file,
        {width: screenW, height: screenH},
        {representation},
        ext
      );

      onNextExitFullscreen(() => {
        stage.dispose();
        if (stageMountRef.current) {
          stageMountRef.current.innerHTML = '';
        }
      });

      try {
        stageMountRef.current?.requestFullscreen?.();
      } catch (e) {
        throw new Error('Fullscreen request invalid: ' + e);
      }
    };

    const titleLink = (
      <SingleRunLink
        {...runSignature}
        linkText={customRunName ?? run.displayName}
      />
    );

    const currentMediaMetadata: Struct | undefined = historyRow?.[mediaKey];
    const captions = useMemo(() => {
      if (props.omitCaptions) {
        return [];
      }
      return makeCaptions(currentMediaMetadata, mediaIndex);
    }, [currentMediaMetadata, mediaIndex, props.omitCaptions]);

    return (
      <Card className="molecule-card media-card group" style={{width}}>
        {labelComponent(props, tileMedia?.step, titleLink)}

        {tileMedia == null ? (
          loading ? (
            <Loader name="panel-content-loader" size="small" />
          ) : (
            <MessageMediaNotFound
              mediaKey={mediaKey}
              stepIndex={globalStep}
              mediaIndex={mediaIndex}
              mediaType="molecule"
            />
          )
        ) : (
          screenshotURL == null && (
            <Placeholder style={{width, height}}>
              <Placeholder.Image />
            </Placeholder>
          )
        )}
        <div ref={stageMountRef}></div>
        {screenshotURL != null && (
          <div
            style={{
              width: '100%',
              height: 'auto',
              overflow: 'auto',
              position: 'relative',
            }}>
            <img
              alt={`Representation of molecule ${mediaKey}`}
              style={{height: 'auto'}}
              src={screenshotURL}
            />
            {/* oxlint-disable-next-line tailwindcss/no-restricted-classes -- grandfathered :has() usage */}
            <div className="media-card__fullscreen [&:has(:focus-visible)]:!text-white [&_button]:!bg-transparent">
              <IconButton
                aria-label="Open molecule fullscreen"
                icon="full-screen-mode-expand"
                iconColor="currentcolor"
                onPress={requestFullscreen}
                variant="ghost"
              />
            </div>
          </div>
        )}
        {/* CAPTIONS */}
        {captions.length > 0 && (
          <div className="image-card-caption">{captions}</div>
        )}
      </Card>
    );
  }
);

export default MoleculeCard;

function getMoleculeFileExt(directURL: string): string {
  const ext = directURL.split('?')[0].split('.').pop();
  if (ext == null) {
    throw new Error('Molecule file must have an extension');
  }
  return ext;
}
